import React, { useMemo } from 'react';

import { PartActionHistoryDataType } from '../../../@types/partHistory.type';
import { ServiceObject } from '../../../@types/service.type';
import { PART_HISTORY_TYPES_FOR_SOLUTION_ACTION } from '../../../constants/part';
import { useGetPartHistoryByServiceIdAndHistoryTypeCodes } from '../../../queries/part-query';
import { SolutionActionMenu, SolutionActionMenuItem } from '../../molecules/SolutionActionMenu';

export interface InteractionSolutionActionFieldProps {
  service: ServiceObject;
  value?: string;
  onChange: (value: string) => void;
  isRequired?: boolean;
}

/**
 * A Solution Action answer: which part action on this job resolved the fault.
 *
 * The options are the job's own **part-action history** — installed, removed,
 * replaced, tested, cleaned — not fault codes. That distinction matters: a Solution
 * Action and a Fault Selection look alike in the template but draw from entirely
 * different sources, and rendering one with the other's options silently collects the
 * wrong answer. `SolutionActionFormFragment` is the legacy renderer this mirrors, and
 * the option shape is deliberately identical to it.
 */
export const InteractionSolutionActionField: React.FC<InteractionSolutionActionFieldProps> = ({
  service,
  value,
  onChange,
  isRequired,
}) => {
  const partHistoryQuery = useGetPartHistoryByServiceIdAndHistoryTypeCodes(
    service.id,
    PART_HISTORY_TYPES_FOR_SOLUTION_ACTION as string[]
  );

  const options = useMemo<SolutionActionMenuItem[]>(
    () =>
      (partHistoryQuery.data ?? []).map((entry: PartActionHistoryDataType) => ({
        value: entry.id.toString(),
        actionName: entry.historyTypeName,
        partType: entry.partTypeName,
        note: entry.notes,
      })),
    [partHistoryQuery.data]
  );

  return (
    <SolutionActionMenu
      options={options}
      value={value ?? ''}
      onChange={onChange}
      error={isRequired && !value}
    />
  );
};
